import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import type { ForwardedRef, ReactNode } from 'react';

import { Box, LinearProgress } from '@mui/material';

import { AgGridReact } from 'ag-grid-react';
import type {
  CellClassParams,
  CellRange,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowGroupOpenedEvent,
} from 'ag-grid-community';

import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import type {
  MergeRowDto,
  MergeSection,
  RowDecisionStatus,
} from '../api/mergeResolution.types';

import {
  FROZEN_WIDTH_VAR,
  COLUMN_WIDTH_MIN,
  PERIOD_COLUMN_WIDTH,
  levelledPeriodWidth,
  periodWidthVarForColId,
} from './gridColumns';
import { useStableColumnDefs } from './stableColumnDefs';

import './conflictGrid.scss';

const LOADING_OVERLAY_TEMPLATE =
  '<span class="ag-overlay-loading-center">Loading Conflicts...</span>';

/**
 * The row-keyed accessors `DecisionSwitchRenderer` reads out of
 * `params.context`. Declared structurally because `DecisionSwitch.tsx` keeps
 * its `DecisionSwitchContext` private.
 */
export interface ConflictGridContext {
  statusFor: (rowId: string) => RowDecisionStatus;
  onTake: (rowId: string, choice: 'MINE' | 'THEIRS') => void;
  onUndo: (rowId: string) => void;
  disabled?: boolean;
}

/**
 * What actually reaches `params.context`: the caller's accessors plus the
 * two things only this component knows (the detail factory and the labels).
 */
interface GridContext extends ConflictGridContext {
  section: MergeSection;
  inputLabel: string;
  detail: (row: MergeRowDto, pane: DetailPane) => ReactNode;
  /**
   * Tells an open detail row that `detail` has changed. AG Grid does not
   * re-render a detail row when the caller's props move, so without this an
   * open row keeps showing the decisions it was first rendered with.
   */
  subscribeDetail: (onChange: () => void) => () => void;
  detailVersion: () => number;
}

/** What the forwarded ref publishes; see `useImperativeHandle` below. */
export interface ConflictGridHandle {
  /** Drops every cell range, as the toolbar's Clear Selection button does. */
  clearSelection: () => void;
  /**
   * The row at a position in the grid's displayed model (after filter and
   * sort), which is what a `CellRange`'s `rowIndex` refers to. `undefined`
   * for a detail row, an out-of-range index, or before the grid is ready.
   */
  rowAtDisplayedIndex: (index: number) => MergeRowDto | undefined;
}

/**
 * Which half of the expanded row is being rendered. With `embedFullWidthRows`
 * the renderer is called once per container, so the detail is split along
 * the freeze boundary: `frozen` sits beside the pinned identity columns,
 * `scrolling` lines up with the period columns and travels with them.
 */
export type DetailPane = 'frozen' | 'scrolling';

/** The grid's measured column geometry. See `onLayoutMeasured`. */
export interface GridLayout {
  /** Summed width of the pinned-left columns: where the freeze boundary is. */
  frozenWidth: number;
  /**
   * Width of a period column (the autofit levels them all). After a manual
   * drag of one period column this is the first one's.
   */
  periodWidth: number;
}

interface ConflictGridProps {
  section: MergeSection;
  inputLabel: string;
  rows: MergeRowDto[];
  columnDefs: ColDef<MergeRowDto>[];
  expandedRowId: string | null;
  onExpand: (rowId: string | null) => void;
  /**
   * Excel-style cell ranges. Fired on every change while a drag is in
   * flight, not only on mouse-up, so a `{n} selected cells` count tracks the
   * drag.
   */
  onRangeChange: (ranges: CellRange[]) => void;
  detail: (row: MergeRowDto, pane: DetailPane) => ReactNode;
  /**
   * The grid's measured column geometry, reported on grid ready, after the
   * one-off autofit, and after a manual column resize. The caller cannot add
   * up the colDef widths instead: the autofit replaces them with measured
   * ones, and the toolbar and expanded row have to line up with the result.
   */
  onLayoutMeasured?: (layout: GridLayout) => void;
  loading: boolean;
  testLayout?: boolean;
  /**
   * The accessors the Decisions column's renderer reads. They live on the
   * grid's `context`, which only the component rendering `AgGridReact` can
   * set. Omitted ⇒ the switches render inert (disabled) rather than throwing
   * on `context.statusFor`.
   */
  context?: ConflictGridContext;
}

// No column-state persistence: sort and filter are per-grid-instance and die
// with the instance.
const GRID_HEIGHT = 420;
const ROW_HEIGHT = 38;

/**
 * How long the data has to stay still before the columns are autofitted.
 * Long enough to outlast the back-to-back bootstrap rows call and the refetch
 * the input rail's auto-selected tab triggers; short enough that the columns
 * are not visibly ragged first.
 */
const AUTO_FIT_SETTLE_MS = 250;

/**
 * Width of the expand-chevron column. Exported because it is part of the
 * frozen block's width, which `gridColumns.pinnedWidth()` cannot include.
 */
export const EXPAND_COLUMN_WIDTH = 60;

const noop = () => undefined;
const fallbackStatusFor = (): RowDecisionStatus => 'PENDING';

/**
 * Master-detail renderer. Module scope keeps its identity stable for the
 * grid's lifetime, and it reads the current `detail` off the shared context
 * object rather than closing over a render-time value.
 */
const NO_SUBSCRIPTION = () => () => undefined;
const NO_VERSION = () => 0;

const DetailRowRenderer = (params: ICellRendererParams<MergeRowDto>) => {
  const row = params.data;
  const context = params.context as GridContext | undefined;
  // Re-renders this row whenever the grid is handed a new `detail`, which it
  // is on every change to the decisions the row displays.
  useSyncExternalStore(
    context?.subscribeDetail ?? NO_SUBSCRIPTION,
    context?.detailVersion ?? NO_VERSION
  );
  if (!row || typeof context?.detail !== 'function') return null;

  // `params.pinned` names the container this copy of the row is being rendered
  // into. There are no right-pinned columns, so nothing belongs in that
  // container and it renders empty rather than duplicating either half.
  if (params.pinned === 'right') return null;
  const pane: DetailPane = params.pinned === 'left' ? 'frozen' : 'scrolling';

  return <>{context.detail(row, pane)}</>;
};

/**
 * The chrome columns — expand chevron, Decisions switch, Status text — must
 * be ignored by a range and must not look selected.
 *
 * AG Grid 31.3.4 has no per-column range exclusion and a range is always a
 * rectangle, so they stay in the range *model*: `api.getCellRanges()` will
 * include them. Instead the keyboard is kept off them (`suppressNavigable`)
 * and this class, via `conflictGrid.scss`, neutralises
 * `.ag-cell-range-selected`. Mapping a range to decisions is the caller's job
 * and must contribute nothing from these columns.
 */
const NO_RANGE_CELL_CLASS = 'mr-cell-no-range';

/**
 * `decisions` and `status` are built by `gridColumns.ts`, so the class is
 * applied here by mapping the incoming `columnDefs` — not through
 * `defaultColDef`, which would mark the period cells the range exists for.
 * These colIds are therefore a contract with that builder.
 */
const NO_RANGE_COL_IDS = new Set(['decisions', 'status']);

/**
 * `decisions` gets the visual treatment but NOT `suppressNavigable`.
 * AG Grid's `findNextCellToFocusOn` skips a suppressed cell for both Tab and
 * arrow keys, and a cell that cannot take focus cannot delegate focus into
 * its children -- so suppressing it would make the Decisions switch, the
 * primary per-row action, reachable by mouse only.
 */
const KEEP_NAVIGABLE_COL_IDS = new Set(['decisions']);

/**
 * Adds a class to a `cellClass` in any of its three shapes, composing rather
 * than overwriting so it stays correct if `gridColumns.ts` starts setting one.
 */
const appendCellClass = (
  existing: ColDef<MergeRowDto>['cellClass'],
  extra: string
): ColDef<MergeRowDto>['cellClass'] => {
  if (existing === undefined) return extra;
  if (typeof existing === 'function') {
    return (params: CellClassParams<MergeRowDto>) => {
      const result = existing(params);
      if (result === null || result === undefined) return extra;
      return Array.isArray(result) ? [...result, extra] : [result, extra];
    };
  }
  return Array.isArray(existing) ? [...existing, extra] : [existing, extra];
};

const withoutRange = (col: ColDef<MergeRowDto>): ColDef<MergeRowDto> => ({
  ...col,
  ...(col.colId !== undefined && KEEP_NAVIGABLE_COL_IDS.has(col.colId)
    ? {}
    : { suppressNavigable: true }),
  cellClass: appendCellClass(col.cellClass, NO_RANGE_CELL_CLASS),
});

/**
 * `api.getCellRanges()` returns the range service's own live array, mutated
 * in place as a drag proceeds (a clear does `cellRanges.length = 0`). A
 * caller holding it in state would never see an identity change and would
 * read grid internals as they move, so only a per-range copy leaves here.
 */
const copyRanges = (ranges: CellRange[] | null): CellRange[] =>
  (ranges ?? []).map((range) => ({ ...range, columns: [...range.columns] }));

/**
 * The expand affordance. The built columns have no `agGroupCellRenderer`
 * cell, without which a master-detail grid cannot open a detail row. It holds
 * only the chevron — AG Grid renders that as a non-tabbable span, which is
 * what keeps `Tab` from the container landing on the first Decisions switch
 * instead of on grid chrome.
 */
const EXPAND_COLUMN: ColDef<MergeRowDto> = {
  colId: 'expand',
  headerName: '',
  cellRenderer: 'agGroupCellRenderer',
  sortable: false,
  filter: false,
  resizable: false,
  suppressMovable: true,
  suppressNavigable: true,
  cellClass: NO_RANGE_CELL_CLASS,
  // Pinned with the rest of the frozen identity block.
  pinned: 'left',
  // Any narrower clips the chevron once the alpine theme's ~17px of cell
  // padding per side is taken out.
  width: EXPAND_COLUMN_WIDTH,
  minWidth: EXPAND_COLUMN_WIDTH,
  maxWidth: EXPAND_COLUMN_WIDTH,
};

const rowIdOf = (node: IRowNode<MergeRowDto>): string | undefined =>
  node.data?.Input_RowID;

// Owner-specified aria labels. The input name is deliberately not included.
const SECTION_ARIA_LABEL: Record<MergeSection, string> = {
  needs_decision: 'Cells needing resolution decision.',
  auto_resolved: 'Cells resolved automatically.',
};

const ConflictGrid = (
  {
    section,
    inputLabel,
    rows,
    columnDefs,
    expandedRowId,
    onExpand,
    onRangeChange,
    detail,
    onLayoutMeasured,
    loading,
    testLayout = false,
    context,
  }: ConflictGridProps,
  ref: ForwardedRef<ConflictGridHandle>
) => {
  const apiRef = useRef<GridApi<MergeRowDto> | null>(null);
  const gridElementRef = useRef<HTMLDivElement | null>(null);

  // True while this component is driving `setExpanded` itself. AG Grid fires
  // `onRowGroupOpened` for programmatic expansion too, so without this guard
  // closing the previously-open row would re-enter the handler and report
  // `onExpand(null)` over the row that was just opened.
  const programmaticRef = useRef(false);

  // The prop, readable from the stable grid callbacks below without
  // rebuilding them on every change.
  const expandedRowIdRef = useRef(expandedRowId);
  expandedRowIdRef.current = expandedRowId;

  const onExpandRef = useRef(onExpand);
  onExpandRef.current = onExpand;

  const onRangeChangeRef = useRef(onRangeChange);
  onRangeChangeRef.current = onRangeChange;

  const onLayoutMeasuredRef = useRef(onLayoutMeasured);
  onLayoutMeasuredRef.current = onLayoutMeasured;

  // The autosize is a one-off: after it the widths are the user's, and
  // re-running it would undo a manual resize on the next refetch.
  const autoSizedRef = useRef(false);

  // The pending settle timer. Cancelled whenever the data moves again, and on
  // unmount so a measurement cannot reach a caller that is gone.
  const autoSizeTimerRef = useRef<number | null>(null);

  /**
   * A forwarded ref rather than an `onReady(api)` callback: `onReady` would
   * leak the whole `GridApi` and invite the caller to drive expansion and
   * ranges behind this component's back, which the reconciliation below
   * exists to own.
   */
  useImperativeHandle(
    ref,
    () => ({
      clearSelection: () => {
        // Null before `onGridReady`. AG Grid itself no-ops, and fires no
        // change event, when there is nothing selected.
        apiRef.current?.clearRangeSelection();
      },
      rowAtDisplayedIndex: (index: number) => {
        const node = apiRef.current?.getDisplayedRowAtIndex(index);
        return node && !node.detail ? node.data : undefined;
      },
    }),
    []
  );

  // Without a context the Decisions switches are inert by design (fail
  // closed rather than throw), which is silent in the UI. Warn in
  // development so a caller that forgets the prop finds out immediately.
  if (process.env.NODE_ENV !== 'production' && !context) {
    // eslint-disable-next-line no-console
    console.warn(
      'ConflictGrid: no `context` prop, so the Decisions column is inert. ' +
        'Pass statusFor/onTake/onUndo to enable it.'
    );
  }

  /**
   * AG Grid snapshots `context` into each cell's `params` when the cell is
   * created, so a fresh object per render would leave already-rendered cells
   * holding stale accessors. The identity is therefore created once and the
   * fields are refreshed in place on every render.
   */
  const detailListenersRef = useRef(new Set<() => void>());
  const detailVersionRef = useRef(0);
  const gridContextRef = useRef<GridContext>({
    section,
    inputLabel,
    detail,
    subscribeDetail: (onChange) => {
      detailListenersRef.current.add(onChange);
      return () => {
        detailListenersRef.current.delete(onChange);
      };
    },
    detailVersion: () => detailVersionRef.current,
    statusFor: context?.statusFor ?? fallbackStatusFor,
    onTake: context?.onTake ?? noop,
    onUndo: context?.onUndo ?? noop,
    disabled: context ? (context.disabled ?? false) : true,
  });
  gridContextRef.current.section = section;
  gridContextRef.current.inputLabel = inputLabel;
  gridContextRef.current.detail = detail;

  // After the render that carries a new `detail`, wake any open detail row.
  useEffect(() => {
    detailVersionRef.current += 1;
    detailListenersRef.current.forEach((onChange) => onChange());
  }, [detail]);
  gridContextRef.current.statusFor = context?.statusFor ?? fallbackStatusFor;
  gridContextRef.current.onTake = context?.onTake ?? noop;
  gridContextRef.current.onUndo = context?.onUndo ?? noop;
  gridContextRef.current.disabled = context
    ? (context.disabled ?? false)
    : true;

  // A decision changes `columnDefs` identity but not the columns; the grid is
  // handed the same defs until the columns change (see `stableColumnDefs`).
  const stableColumnDefs = useStableColumnDefs(columnDefs);

  const allColumnDefs = useMemo(
    () => [
      EXPAND_COLUMN,
      ...stableColumnDefs.map((col) =>
        col.colId !== undefined && NO_RANGE_COL_IDS.has(col.colId)
          ? withoutRange(col)
          : col
      ),
    ],
    [stableColumnDefs]
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<MergeRowDto>) => params.data.Input_RowID,
    []
  );

  /**
   * The grid's measured geometry, read off the api because the autofit
   * changes the colDef widths. Frozen columns are exactly the pinned-left
   * ones and period columns the unpinned ones, so no colId spelling is relied
   * on.
   */
  const reportLayout = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const frozenWidth = api
      .getDisplayedLeftColumns()
      .reduce((total, column) => total + column.getActualWidth(), 0);
    const periods = api.getDisplayedCenterColumns();
    // Published as CSS custom properties as well as reported, so the expanded
    // row tracks a resize with no re-render (see `FROZEN_WIDTH_VAR`). Column
    // ids are `periodColId`s; `periodWidthVar` is keyed off the same id.
    const gridElement = gridElementRef.current;
    if (gridElement) {
      gridElement.style.setProperty(FROZEN_WIDTH_VAR, `${frozenWidth}px`);
      for (const column of periods) {
        gridElement.style.setProperty(
          periodWidthVarForColId(column.getColId()),
          `${column.getActualWidth()}px`
        );
      }
    }
    onLayoutMeasuredRef.current?.({
      frozenWidth,
      periodWidth:
        periods.length > 0 ? periods[0].getActualWidth() : PERIOD_COLUMN_WIDTH,
    });
  }, []);

  /**
   * Fit every column to its contents, once, then level the period columns to
   * a single width.
   *
   * The frozen block should be no wider than the identity it holds, since
   * every pixel of it is taken from the period columns. The header is
   * included in the measurement, so a short column cannot shrink below its
   * title.
   *
   * Period columns all take the widest fitted width (clamped, see
   * `levelledPeriodWidth`): ragged widths make two quarters look different
   * when only their digit counts differ, and `RowDetail` sizes its period
   * cells from one number, so the expanded row only lines up if they are
   * uniform.
   */
  const autoFitColumns = useCallback(() => {
    const api = apiRef.current;
    if (!api || autoSizedRef.current) return;
    if (api.getDisplayedRowCount() === 0) return;

    const frozenIds = api
      .getDisplayedLeftColumns()
      .map((column) => column.getColId())
      // The chevron column is fixed (min = max width).
      .filter((colId) => colId !== EXPAND_COLUMN.colId);
    const periodIds = api
      .getDisplayedCenterColumns()
      .map((column) => column.getColId());

    if (frozenIds.length === 0 && periodIds.length === 0) return;

    autoSizedRef.current = true;

    if (frozenIds.length > 0) {
      api.autoSizeColumns(frozenIds);
    }

    if (periodIds.length > 0) {
      // Fit each to its own content first, so there is something to compare.
      api.autoSizeColumns(periodIds);
      const uniform = levelledPeriodWidth(
        periodIds.map((colId) => api.getColumn(colId)?.getActualWidth() ?? 0)
      );
      api.applyColumnState({
        state: periodIds.map((colId) => ({ colId, width: uniform })),
      });
    }

    reportLayout();
  }, [reportLayout]);

  /**
   * TIMING. `autoSizeColumns` measures rendered text, so before the rows
   * exist it fits every column to its header alone. The grid mounts with
   * `rowData: []` while the session bootstraps, and AG Grid fires both
   * `onRowDataUpdated` and `onFirstDataRendered` for that empty set, so
   * neither means "the data has arrived".
   *
   * What does is the pair this component is given: rows present and no fetch
   * in flight. Rows then land in more than one wave (bootstrap rows, the
   * input rail's auto-selected tab, a debounced refresh), so the measurement
   * waits out a quiet period: any change to `rows` or `loading` clears the
   * timer through this effect's cleanup and restarts the wait.
   *
   * `autoSizedRef` is set only on a measurement that actually ran, so an
   * attempt made while the grid was empty cannot consume the one-off.
   */
  useEffect(() => {
    if (autoSizedRef.current || loading || rows.length === 0) {
      return;
    }

    autoSizeTimerRef.current = window.setTimeout(() => {
      autoSizeTimerRef.current = null;
      autoFitColumns();
    }, AUTO_FIT_SETTLE_MS);

    return () => {
      if (autoSizeTimerRef.current !== null) {
        window.clearTimeout(autoSizeTimerRef.current);
        autoSizeTimerRef.current = null;
      }
    };
  }, [loading, rows, autoFitColumns]);

  /**
   * Reconcile AG Grid's own expansion state with `expandedRowId`. AG Grid has
   * no declarative expansion prop, so the prop is an instruction: grid state
   * is pushed toward it whenever it (or the row set) changes, and the handler
   * below enforces single-expansion immediately, even when the caller feeds
   * the prop straight back unchanged.
   */
  const applyExpansion = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const target = expandedRowIdRef.current;
    programmaticRef.current = true;
    try {
      api.forEachNode((node) => {
        const shouldExpand = target !== null && rowIdOf(node) === target;
        if (Boolean(node.expanded) !== shouldExpand) {
          node.setExpanded(shouldExpand);
        }
      });
    } finally {
      programmaticRef.current = false;
    }
  }, []);

  /**
   * A `CellRange` is positional (`startRow: { rowIndex }`), not id-keyed, so
   * after a page change or a background refresh a held range points at
   * different rows -- acting on it would apply decisions to the wrong cells.
   * Clearing on any row-data change fails closed: the user re-selects rather
   * than silently deciding cells they never looked at.
   */
  const handleRowDataUpdated = useCallback(() => {
    apiRef.current?.clearRangeSelection();
    applyExpansion();
    // No autofit here: this event also fires for the empty mount-time row set.
  }, [applyExpansion]);

  useEffect(() => {
    applyExpansion();
  }, [applyExpansion, expandedRowId]);

  const handleRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<MergeRowDto>) => {
      if (programmaticRef.current) return;

      const rowId = rowIdOf(event.node) ?? null;

      if (event.node.expanded) {
        // Single expansion, enforced here and not left to the round trip
        // through `onExpand` — the caller may hold the prop constant.
        const api = apiRef.current;
        if (api) {
          programmaticRef.current = true;
          try {
            api.forEachNode((node) => {
              if (node.expanded && rowIdOf(node) !== rowId) {
                node.setExpanded(false);
              }
            });
          } finally {
            programmaticRef.current = false;
          }
        }
        if (rowId !== expandedRowIdRef.current) {
          onExpandRef.current(rowId);
        }
        return;
      }

      // Collapse only clears the selection when it is the row the caller
      // believes is open; a stray collapse of some other row must not.
      if (rowId !== null && rowId === expandedRowIdRef.current) {
        onExpandRef.current(null);
      }
    },
    []
  );

  /**
   * A range is positional, so a filter or sort change moves it onto different
   * rows (and a Set Filter hides some of the rows it spans). Same fail-closed
   * rule as a row-data change: drop it and let the user re-select.
   */
  const handleViewChanged = useCallback(() => {
    apiRef.current?.clearRangeSelection();
  }, []);

  // Fired on every change of a drag, not only on mouse-up, and every one is
  // reported so the toolbar's count tracks the drag the way Excel's status
  // bar does.
  const handleRangeSelectionChanged = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    onRangeChangeRef.current(copyRanges(api.getCellRanges()));
  }, []);

  // An empty grid says "Loading Conflicts..." while rows are on their way, and
  // AG Grid's own "No Rows To Show" only once loading has finished. AG Grid
  // shows its no-rows overlay for any empty row set, so the choice is made here
  // rather than left to it. Read through a ref so `handleGridReady` stays
  // stable.
  const overlayStateRef = useRef({ loading, empty: rows.length === 0 });
  overlayStateRef.current = { loading, empty: rows.length === 0 };
  const syncOverlay = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const { loading: isLoading, empty } = overlayStateRef.current;
    if (empty && isLoading) {
      api.showLoadingOverlay();
    } else if (empty) {
      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  }, []);

  useEffect(() => {
    syncOverlay();
  }, [syncOverlay, loading, rows.length]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<MergeRowDto>) => {
      apiRef.current = event.api;
      syncOverlay();
      applyExpansion();
      // The declared widths, so the caller has a geometry to line up against
      // before any rows arrive.
      reportLayout();
    },
    [applyExpansion, reportLayout, syncOverlay]
  );

  /**
   * The Decisions column has no field and no valueGetter, so AG Grid never
   * refreshes it on its own. The column defs are rebuilt whenever the
   * caller's decision state changes, which makes a `columnDefs` identity
   * change the one reliable signal that the accessors' answers moved.
   */
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    // A filter or sort on Status (or any decision-derived value) used to be
    // re-run by the column rebuild a new `columnDefs` caused; the columns are
    // no longer rebuilt, so ask for it. 'filter' runs the filter and then the
    // sort.
    const rowsMayMove =
      api.isAnyFilterPresent() || api.getColumnState().some((c) => c.sort);
    api.refreshClientSideRowModel('filter');
    // A range is positional. With a sort or filter on, the rows it covered may
    // just have moved, and acting on it would decide rows the user never
    // selected, so it is dropped (as a filter or sort change drops it).
    if (rowsMayMove) {
      api.clearRangeSelection();
    }
    api.refreshCells({ force: true });
  }, [columnDefs]);

  const defaultColDef = useMemo<ColDef<MergeRowDto>>(
    () => ({ resizable: true, minWidth: COLUMN_WIDTH_MIN }),
    []
  );

  return (
    <Box
      // `mr-grid` scopes `conflictGrid.scss` to these grids only.
      className="mr-grid ag-theme-alpine"
      ref={gridElementRef}
      role="group"
      tabIndex={0}
      aria-label={SECTION_ARIA_LABEL[section]}
      aria-busy={loading}
      sx={{ width: '100%', height: testLayout ? 'auto' : GRID_HEIGHT }}
    >
      {loading && <LinearProgress variant="indeterminate" />}
      <AgGridReact<MergeRowDto>
        rowData={rows}
        // About half of alpine's default 48px; the grid font is 12px.
        headerHeight={28}
        // 10% under alpine's 42px default row.
        rowHeight={ROW_HEIGHT}
        columnDefs={allColumnDefs}
        defaultColDef={defaultColDef}
        context={gridContextRef.current}
        getRowId={getRowId}
        // `enableFillHandle` stays off: drag-to-fill writes values.
        // `suppressMultiRangeSelection` is deliberately left unset so a
        // Ctrl-drag adds a second range.
        enableRangeSelection
        enableRangeHandle
        masterDetail
        // Without this the detail row renders into `.ag-full-width-container`,
        // which does not scroll horizontally, so its cells drift off the
        // period columns. Embedded, each half scrolls with its own columns,
        // and `DetailRowRenderer` handles the per-container calls.
        embedFullWidthRows
        detailCellRenderer={DetailRowRenderer}
        detailRowAutoHeight
        keepDetailRows={false}
        onRowGroupOpened={handleRowGroupOpened}
        onRowDataUpdated={handleRowDataUpdated}
        onFilterChanged={handleViewChanged}
        onSortChanged={handleViewChanged}
        onRangeSelectionChanged={handleRangeSelectionChanged}
        overlayLoadingTemplate={LOADING_OVERLAY_TEMPLATE}
        onGridReady={handleGridReady}
        onColumnResized={reportLayout}
        // `ensureDomOrder`: the screen reader walk depends on DOM order
        // matching visual order. `enableCellTextSelection` is deliberately NOT
        // set: it lifts `user-select: none`, so a drag would paint the
        // browser's text highlight over the cell tones on top of the range.
        // Copying a value out goes through the row detail's Copy button.
        ensureDomOrder
        domLayout={testLayout ? 'autoHeight' : 'normal'}
        suppressRowVirtualisation={testLayout}
      />
    </Box>
  );
};

export default forwardRef(ConflictGrid);
